import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Network,
  Globe,
  Sprout,
  FileText,
  Trash2,
  Copy,
  Download,
  Upload,
  Loader2,
  Boxes,
  Share2,
  CheckCircle2,
  Rocket,
  SkipForward,
  AlertTriangle,
  PenLine,
  Tags,
  Crown,
  Wrench,
  Clock,
  Link2,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useThematicMaps } from '@/hooks/useThematicMaps';
import { useWebsites } from '@/hooks/useWebsites';
import { useToast } from '@/hooks/use-toast';
import {
  exportMapToJson,
  downloadMapExport,
  importMapFromJson,
  validateMapExportPayload,
  countExportSections,
  type MapExportPayload,
  type ExportOptions,
} from '@/services/mapExportImport';
import type { ThematicMapWithStats, MapPipelineStats } from '@/types/thematicMaps';
import { ExportOptionsDialog } from './ExportOptionsDialog';

/** Relatywny czas po polsku (np. "2 dni temu"). */
function relTimePl(iso?: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'przed chwilą';
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min} min temu`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} godz. temu`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'wczoraj';
  if (d < 7) return `${d} dni temu`;
  if (d < 30) return `${Math.floor(d / 7)} tyg. temu`;
  if (d < 365) return `${Math.floor(d / 30)} mies. temu`;
  return `${Math.floor(d / 365)} lat temu`;
}

/** Stacked progress bar pipeline'u treści (published→ready→brief→working→failed→skipped).
 *  denom = docelowa liczba artykułów (liczba nodów), NIE liczba content-rows — żeby
 *  niewygenerowane nody zostawały jako tło ("do zrobienia"). */
function PipelineBar({ p, denom }: { p: MapPipelineStats; denom: number }) {
  const total = denom || 0;
  if (total === 0) {
    return <div className="h-1.5 w-full rounded-full bg-muted/40" />;
  }
  const seg = (n: number) => `${(n / total) * 100}%`;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted/40">
      <div style={{ width: seg(p.published) }} className="bg-emerald-500" title={`${p.published} opublikowane`} />
      <div style={{ width: seg(p.ready) }} className="bg-blue-500" title={`${p.ready} gotowe`} />
      <div style={{ width: seg(p.brief) }} className="bg-amber-500" title={`${p.brief} brief`} />
      <div style={{ width: seg(p.working) }} className="bg-violet-500" title={`${p.working} w toku`} />
      <div style={{ width: seg(p.failed) }} className="bg-red-500" title={`${p.failed} błąd`} />
      <div style={{ width: seg(p.skipped) }} className="bg-muted-foreground/40" title={`${p.skipped} pominięte`} />
    </div>
  );
}

export function MapsList() {
  const { maps, loading, deleteMap, cloneMap, refreshMaps } = useThematicMaps();
  const { websites } = useWebsites();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterWebsite, setFilterWebsite] = useState<string>('all');
  const [cloning, setCloning] = useState<{ mapId: string; mapName: string } | null>(null);
  const [cloneName, setCloneName] = useState('');

  // Export / Import state
  const [exportingMapId, setExportingMapId] = useState<string | null>(null);
  const [importPayload, setImportPayload] = useState<MapExportPayload | null>(null);
  const [importName, setImportName] = useState('');
  const [importWebsiteId, setImportWebsiteId] = useState<string>('__source__');
  const [importPreserveWp, setImportPreserveWp] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredMaps = maps.filter((m) => {
    if (filterStatus !== 'all' && m.status !== filterStatus) return false;
    if (filterWebsite !== 'all' && m.website_id !== filterWebsite) return false;
    return true;
  });

  const handleDelete = async (e: React.MouseEvent, map: ThematicMapWithStats) => {
    e.stopPropagation();
    if (confirm(`Usunąć mapę "${map.name}"? Ta operacja jest nieodwracalna.`)) {
      await deleteMap(map.id);
    }
  };

  const handleCloneClick = (e: React.MouseEvent, map: ThematicMapWithStats) => {
    e.stopPropagation();
    setCloning({ mapId: map.id, mapName: map.name });
    setCloneName(`${map.name} (kopia)`);
  };

  const handleCloneConfirm = async () => {
    if (!cloning || !cloneName.trim()) return;
    await cloneMap(cloning.mapId, cloneName.trim());
    setCloning(null);
    setCloneName('');
  };

  // ── Export ─────────────────────────────────────────────────────────
  // Dwustopniowy flow: klik → fetch counts + open dialog → user wybiera sekcje
  // → confirm → pelny eksport z wybranymi opcjami.
  const [exportDialog, setExportDialog] = useState<{
    open: boolean;
    mapId: string;
    mapName: string;
    counts: {
      seeds: number; nodes: number; entities: number; entity_attributes: number;
      relations: number; writing_profiles: number; node_content: number;
      knowledge_graphs: number; serp_cache: number;
    };
  } | null>(null);

  const handleExport = async (e: React.MouseEvent, map: ThematicMapWithStats) => {
    e.stopPropagation();
    setExportingMapId(map.id);
    try {
      const counts = await countExportSections(map.id);
      setExportDialog({
        open: true,
        mapId: map.id,
        mapName: map.name,
        counts,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Nie mozna policzyc sekcji', description: msg, variant: 'destructive' });
    } finally {
      setExportingMapId(null);
    }
  };

  const handleExportConfirm = async (opts: ExportOptions) => {
    if (!exportDialog) return;
    setExportingMapId(exportDialog.mapId);
    try {
      const payload = await exportMapToJson(exportDialog.mapId, opts);
      downloadMapExport(payload);
      const c = payload.counts;
      const bits: string[] = [];
      bits.push(`${c.seeds} seeds`);
      bits.push(`${c.nodes} nodes`);
      if (c.relations > 0) bits.push(`${c.relations} rel`);
      if (c.entities > 0) bits.push(`${c.entities} enc`);
      if (c.entity_attributes > 0) bits.push(`${c.entity_attributes} EAV`);
      if (c.node_content > 0) bits.push(`${c.node_content} art.`);
      if (c.knowledge_graphs > 0) bits.push(`${c.knowledge_graphs} KG`);
      toast({
        title: 'Mapa wyeksportowana',
        description: bits.join(' · '),
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Eksport nieudany', description: msg, variant: 'destructive' });
    } finally {
      setExportingMapId(null);
    }
  };

  // ── Import ─────────────────────────────────────────────────────────
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset input żeby można było ponownie wybrać ten sam plik
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      validateMapExportPayload(parsed);
      setImportPayload(parsed);
      setImportName(`${parsed.source_map_name} (import)`);
      setImportWebsiteId('__source__');
      setImportPreserveWp(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({
        title: 'Plik niepoprawny',
        description: msg,
        variant: 'destructive',
      });
    }
  };

  const handleImportConfirm = async () => {
    if (!importPayload || !importName.trim()) return;
    setImporting(true);
    try {
      const websiteId =
        importWebsiteId === '__source__'
          ? undefined
          : importWebsiteId === '__none__'
          ? null
          : importWebsiteId;
      const res = await importMapFromJson(importPayload, {
        newName: importName.trim(),
        websiteId,
        preserveWpMatches: importPreserveWp,
      });
      const c = res.counts;
      const contentBits =
        c.node_content > 0
          ? ` · ${c.node_content} artykułów`
          : '';
      toast({
        title: 'Mapa zaimportowana',
        description: `${c.seeds} seeds · ${c.nodes} nodes · ${c.relations} relacji${contentBits} · WP matches: ${c.wp_matches_preserved}/${c.wp_matches_preserved + c.wp_matches_cleared}`,
      });
      setImportPayload(null);
      await refreshMaps();
      navigate(`/mapy-tematyczne/${res.newMapId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Import nieudany', description: msg, variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-2/3" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtry + Import */}
      <div className="flex gap-3 items-center flex-wrap">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie statusy</SelectItem>
            <SelectItem value="draft">Szkic</SelectItem>
            <SelectItem value="active">Aktywna</SelectItem>
            <SelectItem value="archived">Archiwum</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterWebsite} onValueChange={setFilterWebsite}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Domena" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie domeny</SelectItem>
            {websites.map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={handleImportClick}
          className="ml-auto"
          title="Wrzuć mapę z pliku JSON"
        >
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          Wrzuć mapę z pliku
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
      </div>

      {/* Lista */}
      {filteredMaps.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
          <Network className="h-12 w-12 mb-4 opacity-50" />
          <p className="text-lg font-medium">Brak map tematycznych</p>
          <p className="text-sm">Utwórz nową mapę, aby rozpocząć budowanie SCN.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredMaps.map((map) => (
            <Card
              key={map.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => navigate(`/mapy-tematyczne/${map.id}`)}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium leading-tight">
                  {map.name}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {map.macro_statement && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {map.macro_statement}
                  </p>
                )}

                {/* Meta: domena + Knowledge Graph */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {map.website_name && (
                    <span className="flex items-center gap-1">
                      <Globe className="h-3.5 w-3.5" />
                      {map.website_name}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Sprout className="h-3.5 w-3.5" />
                    {map.seeds_count} seeds
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" />
                    {map.nodes_count} nodes
                  </span>
                  {!!map.entities_count && (
                    <span className="flex items-center gap-1" title="Encje (Knowledge Graph)">
                      <Boxes className="h-3.5 w-3.5" />
                      {map.entities_count} encji
                    </span>
                  )}
                  {!!map.relations_count && (
                    <span className="flex items-center gap-1" title="Relacje między nodami">
                      <Share2 className="h-3.5 w-3.5" />
                      {map.relations_count} rel.
                    </span>
                  )}
                </div>

                {/* Pipeline treści */}
                {(() => {
                  const p: MapPipelineStats =
                    map.pipeline ?? { total: 0, pending: 0, working: 0, brief: 0, ready: 0, published: 0, indexed: 0, skipped: 0, failed: 0 };
                  // Docelowa liczba artykułów = liczba nodów (po audycie to finalna liczba),
                  // a NIE liczba wygenerowanych content-rows. Guard: nigdy mniej niż content total.
                  const target = Math.max(map.nodes_count || 0, p.total);
                  const done = p.published + p.ready;
                  const pct = target ? Math.min(100, Math.round((done / target) * 100)) : 0;
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Treść</span>
                        <span className="font-medium text-foreground">
                          {target === 0 ? 'brak treści' : `${pct}% · ${done}/${target}`}
                        </span>
                      </div>
                      <PipelineBar p={p} denom={target} />
                      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
                        <span className="flex items-center gap-1 text-blue-400" title="Treść gotowa do publikacji (ready)">
                          <CheckCircle2 className="h-3 w-3" />
                          {p.ready} gotowe
                        </span>
                        <span className="flex items-center gap-1 text-emerald-500" title="Opublikowane na WP (published)">
                          <Rocket className="h-3 w-3" />
                          {p.published} opubl.
                        </span>
                        {!!p.published && (
                          <span
                            className={`flex items-center gap-1 ${p.indexed >= p.published ? 'text-sky-400' : 'text-muted-foreground'}`}
                            title="Dodane do indexera (krok Indeksowanie) / opublikowane"
                          >
                            <Link2 className="h-3 w-3" />
                            {p.indexed}/{p.published} w indeksie
                          </span>
                        )}
                        {!!p.skipped && (
                          <span className="flex items-center gap-1 text-muted-foreground" title="Pominięte — matched do istniejącego WP">
                            <SkipForward className="h-3 w-3" />
                            {p.skipped} pomin.
                          </span>
                        )}
                        {!!p.failed && (
                          <span className="flex items-center gap-1 text-red-400" title="Błąd generowania">
                            <AlertTriangle className="h-3 w-3" />
                            {p.failed} błąd
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Wskaźniki: profil / schema / role / naprawa */}
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-border/50 pt-2 text-xs text-muted-foreground">
                  <span
                    className={`flex items-center gap-1 ${map.profile_name ? 'text-foreground' : ''}`}
                    title={map.profile_name ? `Writing Profile: ${map.profile_name}` : 'Brak Writing Profile'}
                  >
                    <PenLine className="h-3 w-3 shrink-0" />
                    <span className="max-w-[150px] truncate">{map.profile_name ?? 'bez profilu'}</span>
                  </span>
                  {map.schema_enabled ? (
                    (map.pipeline?.published ?? 0) > 0 ? (
                      <span
                        className={`flex items-center gap-1 ${
                          (map.schema_count ?? 0) >= (map.pipeline?.published ?? 0)
                            ? 'text-emerald-500'
                            : 'text-amber-500'
                        }`}
                        title="schema.org włączona — artykuły ze schematem / opublikowane"
                      >
                        <Tags className="h-3 w-3" />
                        Schema {map.schema_count ?? 0}/{map.pipeline?.published ?? 0}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-emerald-500" title="schema.org włączona dla domeny">
                        <Tags className="h-3 w-3" />
                        Schema włączona
                      </span>
                    )
                  ) : (
                    <span className="flex items-center gap-1 text-muted-foreground" title="schema.org wyłączona — włącz w zakładce Domeny">
                      <Tags className="h-3 w-3" />
                      Schema niewłączona
                    </span>
                  )}
                  {!!(map.roles && (map.roles.pillar || map.roles.hub)) && (
                    <span className="flex items-center gap-1" title="Pillary · Huby">
                      <Crown className="h-3 w-3" />
                      {map.roles.pillar}P · {map.roles.hub}H
                    </span>
                  )}
                  {map.repair_status && (
                    <span className="flex items-center gap-1 text-violet-400" title={`Ostatnia naprawa: ${relTimePl(map.last_repaired_at)}`}>
                      <Wrench className="h-3 w-3" />
                      napr.
                    </span>
                  )}
                </div>

                {/* Footer: aktywność + akcje */}
                <div className="flex items-center justify-between pt-0.5">
                  <span
                    className="flex items-center gap-1 text-xs text-muted-foreground"
                    title={`Utworzono ${new Date(map.created_at).toLocaleDateString('pl-PL')}`}
                  >
                    <Clock className="h-3 w-3" />
                    {relTimePl(map.last_activity)}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-emerald-600"
                      onClick={(e) => handleExport(e, map)}
                      disabled={exportingMapId === map.id}
                      title="Pobierz mapę jako JSON"
                    >
                      {exportingMapId === map.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-primary"
                      onClick={(e) => handleCloneClick(e, map)}
                      title="Kopiuj mapę (w DB)"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={(e) => handleDelete(e, map)}
                      title="Usuń mapę"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Import dialog */}
      {importPayload && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => !importing && setImportPayload(null)}
        >
          <div
            className="bg-background border rounded-lg p-6 w-[500px] max-w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Upload className="h-5 w-5 text-emerald-600" />
              Import mapy z pliku
            </h3>

            <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-0.5">
              <p>
                <strong>Źródło:</strong> {importPayload.source_map_name}
              </p>
              <p className="text-muted-foreground">
                <strong>Mapa:</strong> {importPayload.counts.seeds} seeds ·{' '}
                {importPayload.counts.nodes} nodes ·{' '}
                {importPayload.counts.entities} entities ·{' '}
                {importPayload.counts.entity_attributes} EAV ·{' '}
                {importPayload.counts.relations} relacji ·{' '}
                {importPayload.counts.writing_profiles} writing profiles
              </p>
              {(importPayload.counts.node_content ?? 0) > 0 && (
                <p className="text-muted-foreground">
                  <strong>Content pipeline:</strong>{' '}
                  {importPayload.counts.node_content} artykułów ·{' '}
                  {importPayload.counts.knowledge_graphs} KG ·{' '}
                  {importPayload.counts.serp_cache} SERP cache
                </p>
              )}
              <p className="text-muted-foreground">
                <strong>Eksport:</strong>{' '}
                {new Date(importPayload.exported_at).toLocaleString('pl-PL')}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Nazwa nowej mapy</label>
              <Input
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
                placeholder="Nazwa nowej mapy"
                autoFocus
                disabled={importing}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Domena</label>
              <Select
                value={importWebsiteId}
                onValueChange={setImportWebsiteId}
                disabled={importing}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__source__">
                    Zachowaj źródłową (
                    {importPayload.source_website_id
                      ? websites.find((w) => w.id === importPayload.source_website_id)?.name ||
                        'nieznana'
                      : 'brak'}
                    )
                  </SelectItem>
                  <SelectItem value="__none__">Bez przypisania domeny</SelectItem>
                  {websites.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={importPreserveWp}
                onCheckedChange={(v) => setImportPreserveWp(!!v)}
                disabled={importing}
              />
              <div className="text-sm space-y-0.5">
                <p className="font-medium leading-tight">
                  Zachowaj dopasowania do wpisów WP
                </p>
                <p className="text-xs text-muted-foreground leading-tight">
                  Tylko jeśli nowa mapa jest pod tą samą domeną. Nieistniejące ID zostaną
                  wyczyszczone automatycznie.
                </p>
              </div>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportPayload(null)}
                disabled={importing}
              >
                Anuluj
              </Button>
              <Button
                size="sm"
                onClick={handleImportConfirm}
                disabled={importing || !importName.trim()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    Importuję...
                  </>
                ) : (
                  <>
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    Importuj
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Clone dialog */}
      {cloning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setCloning(null)}>
          <div className="bg-background border rounded-lg p-6 w-[400px] space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">Kopiuj mapę</h3>
            <p className="text-sm text-muted-foreground">Kopiowanie: "{cloning.mapName}"</p>
            <p className="text-xs text-muted-foreground">Sklonowane zostaną: seeds, nodes (z demand/SERP danymi), encje, relacje.</p>
            <Input
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder="Nazwa nowej mapy"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleCloneConfirm()}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCloning(null)}>Anuluj</Button>
              <Button size="sm" onClick={handleCloneConfirm} disabled={!cloneName.trim()}>
                <Copy className="h-3.5 w-3.5 mr-1" />
                Kopiuj
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Export Options Dialog — pozwala wybrac jakie sekcje eksportowac (do debugowania) */}
      {exportDialog && (
        <ExportOptionsDialog
          open={exportDialog.open}
          onOpenChange={(open) =>
            setExportDialog((prev) => (prev ? { ...prev, open } : null))
          }
          mapName={exportDialog.mapName}
          counts={exportDialog.counts}
          onConfirm={handleExportConfirm}
        />
      )}
    </div>
  );
}
